import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { interviewRuntimeAPI } from '../api/interviewRuntimeAPI';
import { createLiveAvatarRenderer, loadLiveAvatar } from './liveAvatarRenderer';
import { createSimliRenderer, loadSimli } from './simliRenderer';

export { AVATAR_PROVIDERS, avatarTrialProvider } from './avatarTrial';

const READY_TIMEOUT_MS = 12000;

const RENDERERS = { simli: createSimliRenderer, liveavatar: createLiveAvatarRenderer };
const LIBRARIES = { simli: loadSimli, liveavatar: loadLiveAvatar };

/**
 * A lip-synced examiner for one live interview. Opens the provider session
 * once `enabled` (from the start screen, so the face is ready by Begin), and
 * ends it when the interview page goes, so it stops billing.
 *
 * status: 'off' | 'connecting' | 'ready' | 'failed'. whenReady() resolves to
 * the renderer, or null if it failed or took too long, so the examiner's
 * voice can wait for the face rather than start without it, and falls back
 * to plain audio when there is none.
 */
function play(el) {
  try { el.play()?.catch?.(() => { /* autoplay retries when the stream starts */ }); } catch { /* no media support */ }
}

/** The face's own video and audio elements, created once and kept in a tiny
 * invisible holder on the page while no stage shows them: the session opens
 * on the start screen, before the interview screen (and its stage) exists,
 * and must survive the switch between the two. */
function makeElements() {
  const park = document.createElement('div');
  park.setAttribute('aria-hidden', 'true');
  Object.assign(park.style, {
    position: 'fixed', right: '0', bottom: '0', width: '2px', height: '2px',
    opacity: '0.01', overflow: 'hidden', pointerEvents: 'none',
  });
  const video = document.createElement('video');
  video.className = 'is-avatar-video';
  video.autoplay = true;
  video.muted = true; // the voice comes through the audio element
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  const audio = document.createElement('audio');
  audio.autoplay = true;
  park.append(video, audio);
  return { park, video, audio };
}

export function useAvatar(provider, enabled, examiner = null) {
  const elements = useMemo(() => (provider && typeof document !== 'undefined' ? makeElements() : null), [provider]);
  const rendererRef = useRef(null);
  const settleRef = useRef(null);
  const readyRef = useRef(Promise.resolve(null));
  const [status, setStatus] = useState('off');

  useEffect(() => {
    if (!elements) return undefined;
    document.body.appendChild(elements.park);
    return () => elements.park.remove();
  }, [elements]);

  /** Show the face in a stage's host element, and give it back when the stage goes. */
  const dock = useCallback((host) => {
    if (!elements || !host) return;
    host.append(elements.video, elements.audio);
    play(elements.video);
    play(elements.audio);
  }, [elements]);
  const undock = useCallback(() => {
    if (elements) elements.park.append(elements.video, elements.audio);
  }, [elements]);

  // Fetching the provider's code costs nothing, unlike a session, so it
  // starts as soon as the interview has an avatar, before Begin.
  useEffect(() => {
    if (provider) LIBRARIES[provider]?.().catch(() => { /* retried on Begin */ });
  }, [provider]);

  useEffect(() => {
    if (!provider || !enabled || !elements) {
      setStatus('off');
      readyRef.current = Promise.resolve(null);
      return undefined;
    }
    let cancelled = false;
    let session = null;
    setStatus('connecting');
    readyRef.current = new Promise((resolve) => { settleRef.current = resolve; });
    const settle = (renderer) => { settleRef.current?.(renderer); settleRef.current = null; };

    (async () => {
      try {
        session = await interviewRuntimeAPI.startAvatarSession(provider, examiner);
        const renderer = await RENDERERS[provider](session, { video: elements.video, audio: elements.audio });
        if (cancelled) { renderer.stop(); return; }
        rendererRef.current = renderer;
        setStatus('ready');
        settle(renderer);
      } catch {
        if (!cancelled) setStatus('failed');
        settle(null);
      }
    })();

    return () => {
      cancelled = true;
      settle(null);
      rendererRef.current?.stop();
      rendererRef.current = null;
      if (session?.provider === 'liveavatar') {
        interviewRuntimeAPI.stopAvatarSession(session).catch(() => { /* it ends itself when idle */ });
      }
    };
  }, [provider, enabled, elements, examiner]);

  const whenReady = useCallback(() => Promise.race([
    readyRef.current,
    new Promise((resolve) => { setTimeout(() => resolve(null), READY_TIMEOUT_MS); }),
  ]), []);

  return {
    provider, status, video: elements?.video ?? null, audio: elements?.audio ?? null, dock, undock, whenReady,
  };
}
